import { randomUUID } from '@betterment/core';
import { FlowValues, StepProps } from '@betterment/js-runtime';
import { SystemStyleObject } from '@betterment/samba-styled-system/types';

import { FirmQuery } from './graphql/query.firm.generated';

import {
  BillingFrequency,
  BillingPlan as BillingPlanFromSchema,
  BillingRate,
  BillingRateInput,
  FeeConfiguration,
  PaymentType,
  PlatformFeeAllocation,
  PlatformFeePaidBy as PlatformFeePaidFromSchema,
  TieredFeeCalculationMethod,
  UpsertBillingPlanInput,
} from '../../../graphql/schema';

export type ApplyCashFirstBillingModalProps = {
  isOpen: boolean;
  closeHandler: () => void;
  billingPlans: BillingPlanFromSchema[];
  cardName: string;
};

export type SetDefaultModalProps = {
  isOpen: boolean;
  closeHandler: () => void;
  billingPlan: BillingPlan;
  defaultBillingPlan: BillingPlan;
};

export type BillingPlan = {
  id: string;
  paymentType: PaymentType;
  name: string;
};

export type FirmBillingPlan = FirmQuery['firm']['billingPlans'][number];

export type CashFirstBillingPromptCardProps = {
  billingPlans: BillingPlanFromSchema[];
};

export type FeeSummaryBoxProps = {
  billingPlan: UpsertBillingPlanInput;
  platformFee: number;
  numberOfClients?: number;
};

export type ApplyCashFirstBillingModalTableProps = {
  handleCheckboxChange: (value: string) => void;
  billingPlans: BillingPlanFromSchema[];
  selectedOptions: string[];
};

export enum BillingRateType {
  AssetBased = 'BillingRateAssetBased',
  Fixed = 'BillingRateFixed',
  Tiered = 'BillingRateTiered',
}

export enum PlatformFeePaidBy {
  ClientOnly = PlatformFeePaidFromSchema.ClientOnly,
  AdvisorOnly = PlatformFeePaidFromSchema.AdvisorOnly,
  Shared = 'SHARED',
}

export function getPlatformFeePaidBy(platformFeeAllocation: PlatformFeeAllocation): PlatformFeePaidBy {
  if (platformFeeAllocation.__typename === 'SharedPlatformFeeAllocation') {
    return PlatformFeePaidBy.Shared;
  }
  return platformFeeAllocation.paidBy;
}

export const BillingConfigurationTypeMap = {
  BillingRateAssetBased: 'Asset Based',
  BillingRateFixed: 'Fixed',
  BillingRateTiered: 'Tiered',
};

export const PaymentTypeMap = {
  CASH_RESERVE_FIRST: 'Cash Reserve First',
  SUB_ACCOUNT: 'Investments',
};

export interface BillingPlanTableColumn {
  name: string;
  css?: SystemStyleObject;
  tooltip?: React.ReactNode;
}

export type FeeConfigType = 'fixedConfiguration' | 'assetBasedConfiguration';

export type FormTier = {
  uuid: string;
  firmFee: {
    feeConfigType: FeeConfigType;
    feeValue: string;
  };
  tierEnd?: string;
};

export interface BillingPlanFlowProps {
  billingPlanId: string | null;
  planName?: string;
  configurationType?: BillingRate['__typename'];
  paymentType?: PaymentType;
  tiers?: FormTier[];
  tieredFeeCalculationMethod?: TieredFeeCalculationMethod;
  exitToPath: string;
  title: string;
  fixedFeeAmount: string;
  assetBasedFeeRate: string;
  platformFee: number;
  feeConfiguration?: FeeConfiguration;
  billingFrequency: BillingFrequency;
  numberOfClients?: number;
  platformFeePaidBy: PlatformFeePaidBy;
  sharedPlatformFeeClientPortionBps?: string;
}
export type BillingPlanFlowValues = FlowValues<BillingPlanFlowProps>;
export interface BillingPlanStepProps extends StepProps<BillingPlanFlowValues> {
  exitToPath: string;
}

export function getInitialFormValues(
  platformFee: number,
  feeConfiguration: FeeConfiguration,
  billingFrequency: BillingFrequency
): BillingPlanFlowValues {
  return {
    billingPlanId: null,
    planName: '',
    configurationType: 'BillingRateAssetBased',
    paymentType: PaymentType.CashReserveFirst,
    tiers: [
      {
        uuid: randomUUID(),
        firmFee: {
          feeConfigType: 'assetBasedConfiguration',
          feeValue: '',
        },
        tierEnd: '',
      },
      {
        uuid: randomUUID(),
        firmFee: {
          feeConfigType: 'assetBasedConfiguration',
          feeValue: '',
        },
        tierEnd: undefined,
      },
    ],
    tieredFeeCalculationMethod: TieredFeeCalculationMethod.Progressive,
    exitToPath: '/client_billing/billing_plans',
    title: 'Create a billing plan',
    fixedFeeAmount: '',
    assetBasedFeeRate: '',
    platformFee,
    feeConfiguration,
    billingFrequency,
    platformFeePaidBy: PlatformFeePaidBy.ClientOnly,
  };
}

export function getBillingRateType(input: BillingRateInput): BillingRateType {
  if (input.assetBasedConfiguration) return BillingRateType.AssetBased;
  if (input.fixedConfiguration) return BillingRateType.Fixed;
  if (input.tieredConfiguration) return BillingRateType.Tiered;
  throw new Error('Invalid BillingRateInput');
}

export function isExistingPlanWithClients(flowValues: BillingPlanFlowValues): boolean {
  // @ts-expect-error TS18048 violates strict null checks
  return Boolean(flowValues.billingPlanId && flowValues.numberOfClients > 0);
}

export const TIERED_CALCULATION_LABEL: Record<TieredFeeCalculationMethod, string> = {
  [TieredFeeCalculationMethod.Progressive]: 'Progressive',
  [TieredFeeCalculationMethod.Cliff]: 'Cliff-based',
};

export const isTieredBillingRate = (
  rate: BillingPlanFromSchema['billingRate']
): rate is Extract<BillingPlanFromSchema['billingRate'], { __typename: 'BillingRateTiered' }> =>
  rate.__typename === BillingRateType.Tiered;
